import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import SolarIcon from 'components/icons/SolarIcon';
import { formatTaskPayAmount, type TaskPayValue } from '../../shared/TaskPayFields';
import TaskCardPaySection from './general/TaskCardPaySection';

interface TaskCardAdditionalTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
}

const TaskCardAdditionalTab = ({ task, onTaskUpdated }: TaskCardAdditionalTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const payRate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const { showToast } = useToast();
    const [editingPay, setEditingPay] = useState(false);
    const [paySaving, setPaySaving] = useState(false);
    const [noEarlyStartSaving, setNoEarlyStartSaving] = useState(false);
    const [payDraft, setPayDraft] = useState<TaskPayValue>({
        pay: Boolean(task.pay),
        payAmount: task.payAmount ?? null,
        payUsd: task.payUsd !== false,
        showPayToExecutor: Boolean(task.showPayToExecutor),
    });

    const userId = Number(auth?.user?.ID || 0);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isDeleted = Boolean(Number(task.taskDeleted));
    const canEditPay = (isOwner || isController) && !isDeleted;
    const canToggleNoEarlyStart = isOwner && !isDeleted;
    const showPay = canEditPay || Boolean(task.pay);

    useEffect(() => {
        if (editingPay) return;
        setPayDraft({
            pay: Boolean(task.pay),
            payAmount: task.payAmount ?? null,
            payUsd: task.payUsd !== false,
            showPayToExecutor: Boolean(task.showPayToExecutor),
        });
    }, [editingPay, task.ID, task.pay, task.payAmount, task.payUsd, task.showPayToExecutor]);

    const resetPayDraft = () => {
        setPayDraft({
            pay: Boolean(task.pay),
            payAmount: task.payAmount ?? null,
            payUsd: task.payUsd !== false,
            showPayToExecutor: Boolean(task.showPayToExecutor),
        });
    };

    const savePayment = async () => {
        if (!canEditPay || paySaving) return;
        setPaySaving(true);
        const result = await apiTask.updateTaskPayment(task.ID, payDraft);
        setPaySaving(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            resetPayDraft();
            return;
        }
        showToast(t('messPaymentUpdated'), 'success');
        onTaskUpdated(result.task);
        setEditingPay(false);
    };

    const handleNoEarlyStartChange = async (checked: boolean) => {
        if (!canToggleNoEarlyStart || noEarlyStartSaving) return;
        setNoEarlyStartSaving(true);
        const result = await apiTask.updateTaskNoEarlyStart(task.ID, { noEarlyStart: checked });
        setNoEarlyStartSaving(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messNoEarlyStartUpdated'), 'success');
        onTaskUpdated(result.task);
    };

    return (
        <>
            {showPay && (
                <TaskCardPaySection
                    idPrefix={`task-${task.ID}`}
                    editing={editingPay}
                    canEdit={canEditPay}
                    saving={paySaving}
                    value={payDraft}
                    amountLabel={
                        task.pay
                            ? formatTaskPayAmount(Number(task.payAmount || 0), task.payUsd !== false, payRate)
                            : '—'
                    }
                    visibleToExecutor={Boolean(task.pay && task.showPayToExecutor)}
                    onChange={setPayDraft}
                    onStartEdit={() => {
                        resetPayDraft();
                        setEditingPay(true);
                    }}
                    onCancel={() => {
                        resetPayDraft();
                        setEditingPay(false);
                    }}
                    onSave={() => void savePayment()}
                />
            )}

            {(task.reserveExecutorTime ||
                task.executorCanChangeTerm ||
                (task.noEarlyStart && !canToggleNoEarlyStart)) && (
                <section className="task-card-section mb-3">
                    <div className="task-card-panel">
                        <ul className="list-unstyled d-flex flex-column gap-1 mb-0 fs-12 text-muted">
                            {task.reserveExecutorTime ? (
                                <li className="d-flex align-items-center gap-2">
                                    <SolarIcon name="hourglass-split" className="fs-14 flex-shrink-0" />
                                    <span>{t('labelReserveExecutorTime')}</span>
                                </li>
                            ) : null}
                            {task.executorCanChangeTerm ? (
                                <li className="d-flex align-items-center gap-2">
                                    <SolarIcon name="pencil" className="fs-14 flex-shrink-0" />
                                    <span>{t('labelExecutorCanChangeTerm')}</span>
                                </li>
                            ) : null}
                            {task.noEarlyStart && !canToggleNoEarlyStart ? (
                                <li className="d-flex align-items-center gap-2">
                                    <SolarIcon name="lock" className="fs-14 flex-shrink-0" />
                                    <span>{t('labelNoEarlyStart')}</span>
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </section>
            )}

            {canToggleNoEarlyStart && (
                <section className="task-card-section mb-3">
                    <div className="task-card-panel">
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`task-${task.ID}-no-early-start`}
                                checked={Boolean(task.noEarlyStart)}
                                disabled={noEarlyStartSaving}
                                onChange={(e) => void handleNoEarlyStartChange(e.currentTarget.checked)}
                            />
                            <label className="form-check-label" htmlFor={`task-${task.ID}-no-early-start`}>
                                <span className="d-block fs-12">{t('labelNoEarlyStart')}</span>
                                <span className="d-block text-muted fs-12">{t('hintNoEarlyStart')}</span>
                            </label>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default TaskCardAdditionalTab;
